import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Monitor, Trash2 } from 'lucide-react';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { useGetUserSessionsQuery } from '@/features/user-profile/hooks/queries/get-user-sessions.ts';
import { useDeleteUserSessionsMutation } from '@/features/user-profile/hooks/mutations/use-delete-user-sessions.ts';
import { ActiveSessionItem } from './active-session-item.tsx';

export function ActiveSessionsCard() {
  const { data: activeSessions, isLoading } = useGetUserSessionsQuery();

  const sessionCount = activeSessions?.length ?? 0;
  const hasMultiple = sessionCount > 1;

  const { mutate: deleteUserSessions, isPending } = useDeleteUserSessionsMutation();

  const handleTerminateAllOthers = async () => {
    deleteUserSessions();
  };

  return (
    <DashboardCard className="min-h-[320px] border p-4" isLoading={isLoading}>
      <CardHeader className="p-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              Active Sessions
              {sessionCount > 0 && <Badge>{sessionCount}</Badge>}
            </CardTitle>
            <CardDescription>
              Review and manage where your account is signed in. Terminate sessions you don't
              recognize.
            </CardDescription>
          </div>
          {hasMultiple && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleTerminateAllOthers}
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Terminating…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Terminate others
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="max-h-[540px] overflow-y-auto p-0">
        {sessionCount === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-10 text-center">
            <Monitor className="h-8 w-8 opacity-60" />
            <p>No active sessions found.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3 p-2">
            {activeSessions?.map((session) => (
              <ActiveSessionItem key={session.sessionId} session={session} />
            ))}
          </div>
        )}
      </CardContent>
    </DashboardCard>
  );
}
